import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import {
  conversationKeys,
  type ConversationListItem,
} from '@teletienda/shared-ui-lib';
import { conversationsApi } from '@/lib/api/conversations';
import { merchantGuidOf, normalizeUnreadCounts } from '@/lib/messenger/inboxContext';
import type {
  Message,
  SendMessageDto,
  CreateConversationDto,
  ConversationsQueryParams,
  UnreadCounts,
  ViewerContext,
} from '@/types/messenger';

// Re-export for consumers that import from this module
export { conversationKeys };

// --- Conversations List ---

/**
 * One list of one context (and type). The key is `list({ context, type, ... })`, so realtime
 * sync can invalidate every list of a context by the `{ context }` prefix.
 */
export function useConversations(
  params: ConversationsQueryParams & { context: ViewerContext },
  options: { enabled?: boolean } = {}
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: conversationKeys.list(params),
    queryFn: () => conversationsApi.list(params),
    enabled: isAuthenticated && (options.enabled ?? true),
    staleTime: 30 * 1000,
    // A foreign or unknown context is answered 400/403 — the page falls back to «Personal»
    retry: false,
  });
}

// --- Single Conversation ---

/** The detail as seen from `context` — a shop's context reads the shop's unread and counterpart */
function detailKey(conversationGuid: string, context: ViewerContext) {
  return [...conversationKeys.detail(conversationGuid), { context }] as const;
}

export function useConversation(
  conversationGuid: string | undefined,
  context: ViewerContext = 'personal'
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: detailKey(conversationGuid!, context),
    queryFn: () => conversationsApi.get(conversationGuid!, context),
    enabled: isAuthenticated && !!conversationGuid,
    staleTime: 60 * 1000,
  });
}

// --- Unread badges (per context and type) ---

/**
 * `GET /conversations/unread-counts` — the one source of every messenger badge: the context
 * switcher, the tabs and the header indicator. Normalised once here, so no consumer can meet
 * a `NaN`; nothing is recounted on the client (MSG-INBOX-TABS.6 AC-4).
 */
export function useUnreadCounts<T = UnreadCounts>(select?: (counts: UnreadCounts) => T) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: conversationKeys.unreadCounts(),
    queryFn: async () => normalizeUnreadCounts(await conversationsApi.unreadCounts()),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    select,
  });
}

// --- Messages (Infinite Scroll) ---

export function useMessages(conversationGuid: string | undefined) {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: conversationKeys.messages(conversationGuid!),
    queryFn: ({ pageParam }) =>
      conversationsApi.getMessages(conversationGuid!, {
        limit: 30,
        before: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < 30) return undefined;
      // Cursor = oldest message ID in last page
      return lastPage[lastPage.length - 1]?.message_id;
    },
    enabled: isAuthenticated && !!conversationGuid,
    staleTime: Infinity,
  });
}

// --- Send Message (Optimistic) ---

/**
 * Send into a thread. In a shop's context the message goes out with `asMerchantGuid`, so a
 * staff member who also buys in that shop writes as the shop there (ADR-002 D5).
 */
export function useSendMessage(conversationGuid: string, context: ViewerContext = 'personal') {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const asMerchantGuid = merchantGuidOf(context) ?? undefined;

  return useMutation({
    mutationFn: (data: SendMessageDto) =>
      conversationsApi.sendMessage(
        conversationGuid,
        asMerchantGuid ? { ...data, asMerchantGuid } : data
      ),

    onMutate: async (newMessage) => {
      const messagesKey = conversationKeys.messages(conversationGuid);
      await queryClient.cancelQueries({ queryKey: messagesKey });

      const previousMessages = queryClient.getQueryData(messagesKey);

      // Optimistic message
      const optimisticMessage: Message = {
        message_id: `temp-${Date.now()}`,
        conversation_id: conversationGuid,
        sender_guid: user?.userGuid || '',
        sender_type: asMerchantGuid ? 'merchant' : 'user',
        message_type: newMessage.message_type || 'text',
        content: newMessage.content,
        attachments: newMessage.attachments || [],
        status: 'sending',
        read_at: null,
        created_at: new Date().toISOString(),
      };

      queryClient.setQueryData(messagesKey, (old: any) => {
        if (!old) return { pages: [[optimisticMessage]], pageParams: [undefined] };
        return {
          ...old,
          pages: [[optimisticMessage, ...old.pages[0]], ...old.pages.slice(1)],
        };
      });

      return { previousMessages };
    },

    onError: (_err, _variables, _context) => {
      // Mark optimistic message as 'failed' (keeps it visible with retry button)
      const messagesKey = conversationKeys.messages(conversationGuid);
      queryClient.setQueryData(messagesKey, (old: any) => {
        if (!old) return old;
        const firstPage = old.pages[0].map((msg: Message) =>
          msg.message_id.startsWith('temp-') ? { ...msg, status: 'failed' } : msg
        );
        return { ...old, pages: [firstPage, ...old.pages.slice(1)] };
      });
    },

    onSuccess: (sentMessage, variables) => {
      const messagesKey = conversationKeys.messages(conversationGuid);

      if (variables.context) {
        // Context sends create a system_notification + user message on the backend.
        // The API only returns the user message, so invalidate the cache to refetch
        // both messages (system_notification will appear above the user's message).
        queryClient.invalidateQueries({ queryKey: messagesKey });
      } else {
        // Replace optimistic message with real one
        queryClient.setQueryData(messagesKey, (old: any) => {
          if (!old) return old;
          const firstPage = old.pages[0].map((msg: Message) =>
            msg.message_id.startsWith('temp-') ? sentMessage : msg
          );
          return { ...old, pages: [firstPage, ...old.pages.slice(1)] };
        });
      }

      // Update conversation list (last message preview)
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Create Conversation ---

export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateConversationDto) => conversationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Mark As Read ---

export function useMarkAsRead(conversationGuid: string, context: ViewerContext = 'personal') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => conversationsApi.markAsRead(conversationGuid, context),

    onMutate: async () => {
      // Optimistically set unreadCount to 0 in conversations list
      await queryClient.cancelQueries({ queryKey: conversationKeys.lists() });

      const previousLists = queryClient.getQueriesData<ConversationListItem[]>({
        queryKey: conversationKeys.lists(),
      });

      queryClient.setQueriesData<ConversationListItem[]>(
        { queryKey: conversationKeys.lists() },
        (old) => {
          if (!old) return old;
          return old.map((conv) =>
            conv.conversationId === conversationGuid
              ? { ...conv, unreadCount: 0, hasUnreadMessages: false }
              : conv
          );
        }
      );

      // Also patch detail query to prevent useEffect(hasUnreadMessages) re-trigger
      const previousDetail = queryClient.getQueryData(detailKey(conversationGuid, context));
      queryClient.setQueryData(
        detailKey(conversationGuid, context),
        (old: any) => old ? { ...old, hasUnreadMessages: false, unreadCount: 0 } : old
      );

      return { previousLists, previousDetail };
    },

    onError: (_err, _variables, snapshot) => {
      if (snapshot?.previousLists) {
        for (const [key, data] of snapshot.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      if (snapshot?.previousDetail) {
        queryClient.setQueryData(detailKey(conversationGuid, context), snapshot.previousDetail);
      }
    },

    onSuccess: () => {
      // Badges (selector, tabs, header dots) come from the server only
      queryClient.invalidateQueries({ queryKey: conversationKeys.unreadCounts() });
    },

    onSettled: () => {
      // `onMutate` cancels in-flight lists. A list still on its first fetch (a deep link to an
      // unread thread) is reverted to «no data, not fetching» and nothing would ask again —
      // the page would show «no conversations». Refetch once the read has landed.
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Close Conversation ---

export function useCloseConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationGuid,
      reason,
      context,
    }: {
      conversationGuid: string;
      reason?: string;
      /** `merchant:<g>` — close as that shop */
      context?: ViewerContext;
    }) => conversationsApi.close(conversationGuid, reason, context),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Update Conversation ---

export function useUpdateConversation(conversationGuid: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof conversationsApi.update>[1]) =>
      conversationsApi.update(conversationGuid, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(conversationKeys.detail(conversationGuid), updated);
      queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}

// --- Unread Total (derived) ---

/**
 * Every unread of the viewer — «Personal» and each shop — for the mobile menu's number.
 * Derived from `unread-counts`; there is no second request and no client-side recount.
 */
export function useUnreadTotal() {
  return useUnreadCounts(
    (counts) => counts.personal.total + counts.merchants.reduce((sum, m) => sum + m.total, 0)
  );
}
